import { createSvgEl } from './svg';
import { type Vec2 } from '@learnforge/demo-math/geometry';

export interface DragSpec {
  /** Projects the raw pointer position into the allowed set (e.g. onto a circle, the lattice). */
  constraint?: (p: Vec2) => Vec2;
  /** Magnetism to nice states; returns the snapped position or null when no snap applies. */
  snap?: (p: Vec2) => Vec2 | null;
}

export interface DragPointOptions {
  parent: SVGElement;
  at: Vec2;
  spec?: DragSpec;
  onMove: (at: Vec2) => void;
  /** Maps a raw pointer (clientX/clientY) into world coords. Identity by default (tests use identity). */
  screenToWorld?: (screen: Vec2) => Vec2;
  radius?: number;
  className?: string;
}

export interface DragPointHandle {
  element: SVGElement;
  update(at: Vec2): void;
  destroy(): void;
}

export function createDragPoint(options: DragPointOptions): DragPointHandle {
  const {
    parent,
    at,
    spec,
    onMove,
    screenToWorld = (p: Vec2) => p,
    radius = 7,
    className = '',
  } = options;

  let currentPos = at;
  let isCapturing = false;

  const element = createSvgEl('circle', {
    cx: currentPos.x,
    cy: currentPos.y,
    r: radius,
    class: `geo-drag-point ${className}`,
    tabindex: '0',
    role: 'slider',
  });

  parent.appendChild(element);

  const applyConstraint = (p: Vec2): Vec2 => {
    return spec?.constraint ? spec.constraint(p) : p;
  };

  const applySnap = (p: Vec2): Vec2 => {
    const snapped = spec?.snap ? spec.snap(p) : null;
    return snapped !== null ? snapped : p;
  };

  const processPosition = (p: Vec2): Vec2 => {
    const constrained = applyConstraint(p);
    return applySnap(constrained);
  };

  const updateVisual = (p: Vec2): void => {
    currentPos = p;
    element.setAttribute('cx', String(p.x));
    element.setAttribute('cy', String(p.y));
  };

  const handlePointerDown = (evt: PointerEvent): void => {
    isCapturing = true;
    try {
      element.setPointerCapture?.(evt.pointerId);
    } catch {
      // jsdom may not implement setPointerCapture
    }
  };

  const handlePointerMove = (evt: PointerEvent): void => {
    if (!isCapturing) return;

    const screenPos = { x: evt.clientX, y: evt.clientY };
    const worldPos = screenToWorld(screenPos);
    const processed = processPosition(worldPos);

    updateVisual(processed);
    onMove(processed);
  };

  const handlePointerUp = (): void => {
    isCapturing = false;
  };

  const handleKeyDown = (evt: KeyboardEvent): void => {
    let nudged: Vec2 | null = null;

    switch (evt.key) {
      case 'ArrowRight':
        nudged = { x: currentPos.x + 1, y: currentPos.y };
        evt.preventDefault();
        break;
      case 'ArrowLeft':
        nudged = { x: currentPos.x - 1, y: currentPos.y };
        evt.preventDefault();
        break;
      case 'ArrowUp':
        nudged = { x: currentPos.x, y: currentPos.y - 1 };
        evt.preventDefault();
        break;
      case 'ArrowDown':
        nudged = { x: currentPos.x, y: currentPos.y + 1 };
        evt.preventDefault();
        break;
    }

    if (nudged !== null) {
      const processed = processPosition(nudged);
      updateVisual(processed);
      onMove(processed);
    }
  };

  element.addEventListener('pointerdown', handlePointerDown);
  document.addEventListener('pointermove', handlePointerMove);
  document.addEventListener('pointerup', handlePointerUp);
  element.addEventListener('keydown', handleKeyDown);

  const handle: DragPointHandle = {
    element,

    update(at: Vec2): void {
      updateVisual(at);
    },

    destroy(): void {
      element.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
      element.removeEventListener('keydown', handleKeyDown);

      if (element.parentNode) {
        element.parentNode.removeChild(element);
      }
    },
  };

  return handle;
}
